import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, ArrowLeft, ShieldCheck } from "lucide-react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const tokenId = params.get("id") || "";
  const token   = params.get("token") || "";
  const validLink = !!(tokenId && token);

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const strength = useMemo(() => {
    let s = 0;
    if (pw1.length >= 8) s++;
    if (pw1.length >= 12) s++;
    if (/[A-Z]/.test(pw1) && /[a-z]/.test(pw1)) s++;
    if (/[0-9]/.test(pw1)) s++;
    if (/[^A-Za-z0-9]/.test(pw1)) s++;
    return s;
  }, [pw1]);
  const strengthLabel = ["Très faible", "Faible", "Moyen", "Bon", "Fort", "Très fort"][strength];
  const strengthColor = ["bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-lime-500", "bg-green-500", "bg-emerald-400"][strength];

  const submit = async (e) => {
    e.preventDefault();
    if (pw1.length < 8) { toast.error("8 caractères minimum"); return; }
    if (pw1 !== pw2) { toast.error("Les mots de passe ne correspondent pas"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token_id: tokenId, token, new_password: pw1 });
      toast.success("Mot de passe mis à jour ! Connecte-toi avec le nouveau.");
      setTimeout(() => navigate("/login"), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Lien invalide ou expiré");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-white/55 hover:text-yellow-400 transition mb-6">
          <ArrowLeft size={14} /> Connexion
        </Link>

        <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-7 md:p-9">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={28} className="text-yellow-400" />
            <h1 className="font-black text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "'Anton', sans-serif" }}>
              Nouveau mot de passe
            </h1>
          </div>

          {!validLink ? (
            <div className="py-6 text-center space-y-3">
              <p className="text-white/70 text-sm">Lien de réinitialisation invalide ou incomplet.</p>
              <Link to="/forgot-password" className="inline-block px-5 py-2.5 bg-yellow-400 text-black font-black uppercase tracking-widest text-xs transition hover:bg-yellow-300">
                Demander un nouveau lien
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4 mt-4">
              <div>
                <div className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 focus-within:border-yellow-400/50">
                  <Lock size={16} className="text-white/55" />
                  <input
                    type={show ? "text" : "password"}
                    required
                    autoFocus
                    value={pw1}
                    onChange={(e) => setPw1(e.target.value)}
                    placeholder="Nouveau mot de passe (8 caractères min)"
                    minLength={8}
                    className="flex-1 bg-transparent outline-none text-sm"
                    data-testid="reset-pw1"
                  />
                  <button type="button" onClick={() => setShow((s) => !s)} className="text-white/45 hover:text-white" aria-label="Afficher">
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {pw1 && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div className={`h-full ${strengthColor} transition-all`} style={{ width: `${(strength / 5) * 100}%` }} />
                    </div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-white/55">{strengthLabel}</p>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 focus-within:border-yellow-400/50">
                <Lock size={16} className="text-white/55" />
                <input
                  type={show ? "text" : "password"}
                  required
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  placeholder="Confirme le mot de passe"
                  minLength={8}
                  className="flex-1 bg-transparent outline-none text-sm"
                  data-testid="reset-pw2"
                />
              </div>

              {pw2 && pw1 !== pw2 && <p className="text-xs text-red-400">Les mots de passe ne correspondent pas.</p>}

              <button
                type="submit"
                disabled={loading || pw1.length < 8 || pw1 !== pw2}
                className="w-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-50 text-black font-black uppercase tracking-widest text-sm py-3 transition"
                data-testid="reset-submit-btn"
              >
                {loading ? "Mise à jour…" : "Définir le nouveau mot de passe"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
